import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { accountService } from "../../../../services/api/accountService";
import { firebaseService } from "../../../../services/api/firebaseService";
import {
  convertDateFormat,
  formatDate,
  formatDateInput,
  reverseDateFormat,
} from "../../../../utils/DateFormat";
import { Field } from "../../../components/Field";
import { SelectField } from "../../../components/SelectField";
import DatePicker from "../../components/DatePicker";
import Loading from "../../components/Loading";
import { StatusBadge } from "../../../../constants/statusConstants";

const GENDER_OPTIONS = [
  { value: "Nam", label: "Nam" },
  { value: "Nữ", label: "Nữ" },
  { value: "Khác", label: "Khác" },
];

const UserProfile = () => {
  const navigate = useNavigate();
  const { token, user, dispatch } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [fetchLoading, setFetchLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState("/images/avatar-default.png");
  const [imageFile, setImageFile] = useState(null);
  const [data, setData] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const onChange = (key, value) => setData((prev) => ({ ...prev, [key]: value }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Chỉ chấp nhận định dạng JPEG/PNG.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Kích thước tệp vượt quá 25MB.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setImageSrc(reader.result || "/images/avatar-default.png");
    reader.onerror = () => toast.error("Không thể đọc tệp. Vui lòng thử lại.");
    reader.readAsDataURL(file);
    setImageFile(file);
  };

  const buildViewData = (res) => ({
    avatarUrl: res.avatarUrl || "",
    fullName: res.fullName || "",
    email: res.email || "",
    phone: res.phone || "",
    gender: res.gender || "Nam",
    dateOfBirth: res.dateOfBirth ? new Date(res.dateOfBirth) : null,
    dateOfBirthStr: res.dateOfBirth ? formatDateInput(res.dateOfBirth) : "",
    onBoardDate: res.joinDate ? new Date(res.joinDate) : null,
    onBoardDateStr: res.joinDate ? formatDateInput(res.joinDate) : "",
    province: res.address?.province || "",
    district: res.address?.district || "",
    address: res.address?.detail || "",
    staffCode: res.staffCode || "",
    username: res.username || "",
    status: res.status || "",
    website: res.website || "",
    roleLabel: res.role?.displayName || res.role?.name || "",
    department: res.department?.name || "",
    responsibleDepartments: (res.responsibleDepartments || []).map((d) => d.name || d).filter(Boolean),
    password: "",
  });

  const fetchUserData = useCallback(async () => {
    try {
      setFetchLoading(true);
      const res = await accountService.get_me(token);
      const response = res.data;
      setRawData(response);

      const user_data = {
        identifier: user?.identifier,
        id: user?.id,
        MST: response.MST,
        avatarUrl: response.avatarUrl,
        fullName: response.fullName,
        companyName: response.companyName,
        username: response.username,
        phone: response.phone,
        typeAccount: user?.typeAccount,
        role: response.role?.name,
        email: response.email,
      };
      if (JSON.stringify(user) !== JSON.stringify(user_data)) {
        dispatch({ type: "UPDATE_USER_INFO", payload: { user: user_data } });
      }

      setData(buildViewData(response));
      setImageSrc(response.avatarUrl || "/images/avatar-default.png");
    } catch (error) {
      toast.error(
        error.response
          ? error.response?.data?.message
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setFetchLoading(false);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleEdit = () => {
    if (rawData) setData(buildViewData(rawData));
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (rawData) setData(buildViewData(rawData));
    setImageSrc(rawData?.avatarUrl || "/images/avatar-default.png");
    setImageFile(null);
    setIsEditMode(false);
  };

  const handleSubmit = async () => {
    if (!data.fullName || !data.email) {
      toast.error("Vui lòng nhập đầy đủ thông tin bắt buộc");
      return;
    }

    try {
      setSaveLoading(true);

      let avatarUrl = data.avatarUrl;
      if (imageFile) {
        if (data.avatarUrl && data.avatarUrl.startsWith("https://")) {
          await firebaseService.delete_file(token, data.avatarUrl);
        }
        const uploadRes = await firebaseService.upload_file(token, imageFile);
        if (!uploadRes?.url) {
          toast.error("Upload avatar thất bại");
          return;
        }
        avatarUrl = uploadRes.url;
      }

      const payload = {
        fullName: data.fullName,
        gender: data.gender,
        email: data.email,
        phone: data.phone,
        address: {
          province: data.province,
          city: data.province,
          district: data.district,
          detail: data.address,
        },
        username: data.username,
        joinDate: data.onBoardDateStr ? convertDateFormat(data.onBoardDateStr) : undefined,
        avatarUrl,
      };

      if (user?.role === "Manager" && data.website) {
        payload.website = data.website;
      }
      if (data.dateOfBirthStr) {
        payload.dateOfBirth = convertDateFormat(data.dateOfBirthStr);
      }
      if (data.password?.trim()) {
        payload.password = data.password;
      }

      await accountService.update(payload, token);
      toast.success("Cập nhật thông tin thành công!");

      await fetchUserData();
      setIsEditMode(false);
      setImageFile(null);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  if (fetchLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-50">
      <div className="mx-auto bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-4">
          <h1 className="text-xl font-bold">Tài khoản của tôi</h1>
        </div>

        <div className="p-6">
          <div className="flex flex-col items-center mb-6">
            <div className="relative w-32 h-32">
              <div className="w-full h-full rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                <img src={imageSrc} alt="Avatar" className="w-full h-full object-cover" />
              </div>
              {isEditMode && (
                <>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-full bg-black/30 cursor-pointer">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" onChange={handleImageChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                </>
              )}
            </div>
            <h3 className="mt-3 text-lg font-semibold text-gray-900">{data.fullName || "---"}</h3>
            <p className="text-sm text-blue-600 font-medium">{data.roleLabel || user?.role || ""}</p>
            <p className="text-sm text-gray-500">{data.email || ""}</p>
          </div>

          {/* Form fields */}
          <div className="grid grid-cols-2 gap-5">
            <Field
              label="Họ và tên"
              required
              value={data.fullName}
              disabled={!isEditMode}
              placeholder="Nhập họ và tên"
              onChange={(v) => onChange("fullName", v)}
            />

            <Field
              label="Email"
              required
              value={data.email}
              disabled={!isEditMode}
              placeholder="Nhập email"
              onChange={(v) => onChange("email", v)}
            />

            <Field
              label="Số điện thoại"
              value={data.phone}
              disabled={!isEditMode}
              placeholder="Nhập số điện thoại"
              onChange={(v) => onChange("phone", v)}
            />

            <SelectField
              label="Giới tính"
              disabled={!isEditMode}
              options={GENDER_OPTIONS}
              value={GENDER_OPTIONS.find((o) => o.value === data.gender)}
              onChange={(opt) => onChange("gender", opt?.value)}
            />

            <div>
              <label className="block text-sm text-gray-600 mb-1">Ngày sinh</label>
              {isEditMode ? (
                <DatePicker
                  value={data.dateOfBirthStr}
                  onChange={(str) =>
                    setData((prev) => ({
                      ...prev,
                      dateOfBirthStr: str,
                      dateOfBirth: str ? new Date(reverseDateFormat(str)) : null,
                    }))
                  }
                  width="100%"
                  placeholder="Chọn ngày"
                  height="52px"
                />
              ) : (
                <div className="text-sm font-medium mt-2">
                  {formatDate(data.dateOfBirth) || "--"}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Ngày tham gia</label>
              <div className="text-sm font-medium mt-2">
                {formatDate(data.onBoardDate) || "--"}
              </div>
            </div>

            <Field
              label="Mã nhân viên"
              value={data.staffCode}
              disabled
            />

            <Field
              label="Tên tài khoản"
              value={data.username}
              disabled={!isEditMode}
              placeholder="Nhập tên tài khoản"
              onChange={(v) => onChange("username", v)}
            />

            <div>
              <label className="block text-sm text-gray-600 mb-1">Trạng thái</label>
              <div className="mt-2">
                {isEditMode
                  ? <div className="text-sm font-medium">{data.status || "--"}</div>
                  : <StatusBadge status={data.status} />
                }
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Chức vụ</label>
              <div className="text-sm font-medium mt-2">
                {data.roleLabel || user?.role || "--"}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Phòng ban trực thuộc</label>
              <div className="text-sm font-medium mt-2">
                {data.department || "--"}
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm text-gray-600 mb-1">Phòng ban phụ trách</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {data.responsibleDepartments?.length > 0 ? (
                  data.responsibleDepartments.map((name, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium border border-blue-100"
                    >
                      {name}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-400">Chưa có</span>
                )}
              </div>
            </div>

            {isEditMode && (
              <Field
                label="Mật khẩu mới"
                value={data.password}
                type="password"
                placeholder="Để trống nếu không đổi"
                onChange={(v) => onChange("password", v)}
              />
            )}

            {user?.role === "Manager" && (
              <Field
                label="Website"
                value={data.website}
                disabled={!isEditMode}
                placeholder="Nhập website"
                onChange={(v) => onChange("website", v)}
              />
            )}
          </div>

          {/* Address section */}
          <div className="mt-6 pt-5 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Địa chỉ
            </h4>
            <div className="grid grid-cols-2 gap-5">
              <Field
                label="Tỉnh / Thành phố"
                value={data.province}
                disabled={!isEditMode}
                placeholder="Nhập tỉnh / thành phố"
                onChange={(v) => onChange("province", v)}
              />
              <Field
                label="Quận / Huyện"
                value={data.district}
                disabled={!isEditMode}
                placeholder="Nhập quận / huyện"
                onChange={(v) => onChange("district", v)}
              />
              <div className="col-span-2">
                <Field
                  label="Địa chỉ chi tiết"
                  value={data.address}
                  disabled={!isEditMode}
                  placeholder="Nhập địa chỉ chi tiết"
                  onChange={(v) => onChange("address", v)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-white">
          {isEditMode ? (
            <>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saveLoading}
                className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-all duration-200"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saveLoading}
                className={`px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium transition-all duration-200 shadow-sm ${saveLoading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {saveLoading ? (
                  <div className="flex items-center gap-2">
                    <Loading size="small" />
                    <span>Đang lưu...</span>
                  </div>
                ) : "Lưu thay đổi"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleEdit}
              className="px-6 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium flex items-center gap-2 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Chỉnh sửa thông tin
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
